// Component: BacktestMetrics.jsx
// Purpose: Backtest performance metric cards row
import React from 'react';
import { FiTrendingUp, FiTrendingDown, FiPercent, FiActivity, FiHash, FiDollarSign, FiAlertTriangle } from 'react-icons/fi';

//! 144-1 R5 — plain-language labels for the run's cost-blind reasons (metrics.economic_provenance,
//  written by strategy-management at create and completed by the backtest engine). Unknown reasons
//  render verbatim so a new reason is never hidden.
const COST_BLIND_LABELS = {
    zero_brokerage: 'no brokerage charged',
    zero_slippage_pct: 'no percentage slippage',
    no_trigger_slip_calibration: 'no calibrated stop/target slippage for this instrument',
};

// --> "0.03 (user)" style value + source line for one provenance entry
const describeCost = (entry) => {
    if (!entry || entry.value === undefined || entry.value === null) return 'not reported';
    return `${entry.value}% (${entry.source || 'unreported'})`;
};

// --> Run cost provenance + cost-blind flag (R5 AC5.1 / AC5.2)
const CostProvenance = ({ provenance }) => {
    if (!provenance || typeof provenance !== 'object') return null;
    const reasons = Array.isArray(provenance.cost_blind_reasons) ? provenance.cost_blind_reasons : [];
    const triggerSource = provenance.trigger_slip?.source || 'not reported';
    return (
        <div
            data-testid="backtest-cost-provenance"
            className={`col-span-full rounded-xl px-3 py-2 border text-[11px] ${provenance.cost_blind ? 'border-[var(--warning)] text-[var(--warning)]' : 'border-[var(--divider)] text-fg-3'}`}
        >
            {provenance.cost_blind && (
                <p className="font-bold flex items-center gap-1">
                    <FiAlertTriangle size={12} /> Cost-blind run: {reasons.map((r) => COST_BLIND_LABELS[r] || r).join('; ')}
                </p>
            )}
            <p>
                Brokerage {describeCost(provenance.brokerage)} · Slippage {describeCost(provenance.slippage)} · Stop/target slippage: {triggerSource}
            </p>
        </div>
    );
};

const asNumber = (value, fallback = 0) => {
    const n = Number(value);
    return Number.isFinite(n) ? n : fallback;
};

const normalizeMetrics = (metrics = {}) => {
    return {
        netProfit: asNumber(metrics.net_profit ?? metrics.netProfit ?? metrics.total_return ?? metrics.totalReturn),
        winRate: asNumber(metrics.winRate ?? metrics.win_rate),
        sharpeRatio: asNumber(metrics.sharpeRatio ?? metrics.sharpe_ratio),
        maxDrawdown: asNumber(metrics.maxDrawdown ?? metrics.max_drawdown),
        totalTrades: asNumber(metrics.totalTrades ?? metrics.total_trades),
        profitFactor: asNumber(metrics.profitFactor ?? metrics.profit_factor),
    };
};

// --> Stacked layout: icon left, title + value + trend right — no horizontal overflow
const MetricCard = ({ title, value, icon: Icon, trend, prefix = '', suffix = '', colorClass = 'text-[var(--node-visual)]' }) => (
    <div className="bg-bg-2 rounded-xl px-3 py-3 border border-[var(--divider)] shadow-sm flex items-start gap-3 min-w-0">
        <div className={`p-2 rounded-lg bg-bg-1 shrink-0 mt-0.5 ${colorClass}`}>
            <Icon size={16} />
        </div>
        <div className="min-w-0 flex-1">
            <p className="text-[9px] font-bold text-fg-3 uppercase tracking-widest leading-tight mb-0.5 truncate">{title}</p>
            <p className={`text-sm font-black font-mono leading-tight truncate ${colorClass}`}>
                {prefix}{value}{suffix}
            </p>
            {trend !== undefined && trend !== 0 && (
                <p className={`text-[10px] font-bold mt-0.5 ${trend > 0 ? 'text-[var(--color-profit)]' : 'text-[var(--color-loss)]'}`}>
                    {trend > 0 ? '▲' : '▼'} {Math.abs(asNumber(trend)).toFixed(2)}%
                </p>
            )}
        </div>
    </div>
);

const BacktestMetrics = ({ metrics }) => {
    const data = normalizeMetrics(metrics);
    const returnPct = asNumber(metrics?.total_return_pct ?? metrics?.totalReturnPct);

    return (
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
            <MetricCard
                title="Net Profit"
                value={data.netProfit.toLocaleString('en-IN', { maximumFractionDigits: 2 })}
                prefix="₹"
                icon={data.netProfit >= 0 ? FiTrendingUp : FiTrendingDown}
                colorClass={data.netProfit >= 0 ? 'text-[var(--color-profit)]' : 'text-[var(--color-loss)]'}
                trend={returnPct !== 0 ? returnPct : undefined}
            />
            <MetricCard
                title="Win Rate"
                value={data.winRate.toFixed(2)}
                suffix="%"
                icon={FiPercent}
                colorClass="text-[var(--node-visual)]"
            />
            <MetricCard
                title="Sharpe Ratio"
                value={data.sharpeRatio.toFixed(2)}
                icon={FiActivity}
                colorClass="text-[var(--node-comparator)]"
            />
            <MetricCard
                title="Max Drawdown"
                value={Math.abs(data.maxDrawdown).toFixed(2)}
                suffix="%"
                icon={FiTrendingDown}
                colorClass="text-[var(--warning)]"
            />
            <MetricCard
                title="Total Trades"
                value={data.totalTrades}
                icon={FiHash}
                colorClass="text-[var(--node-math)]"
            />
            <MetricCard
                title="Profit Factor"
                value={data.profitFactor.toFixed(2)}
                icon={FiDollarSign}
                colorClass="text-[var(--color-accent)]"
            />
            <CostProvenance provenance={metrics?.economic_provenance} />
        </div>
    );
};

export default BacktestMetrics;
